import { CommonModule } from '@angular/common';
import { Component, HostListener, Inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ControleEstoqueService } from '../../services/apis/produto/controle-estoque.service';
import { ControleEstoqueDetalheSeriesRequest } from '../../services/requests/produto/controle-estoque-detalhe-series.request';

export interface SerieSaidaEstoqueModalData {
  idProduto: number;
  idLocalizacaoEstoque: number;
  nomeProduto: string;
  skuProduto?: string;
  quantidadeTotal: number;
  series: SerieSaidaEstoqueModalItem[];
}

export interface SerieSaidaEstoqueModalItem {
  idSerie: number;
  codigoSerie: string;
  selecionada: boolean;
}

@Component({
  selector: 'app-serie-saida-estoque-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatCheckboxModule,
    MatTableModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './serie-saida-estoque-modal.component.html',
  styleUrl: './serie-saida-estoque-modal.component.css',
})
export class SerieSaidaEstoqueModalComponent implements OnInit {
  public nomeProduto: string = '';
  public skuProduto: string = '';
  public quantidadeTotal: number = 0;
  public isLoading: boolean = false;
  public erroCarga: string | null = null;
  public bipeInput: string = '';
  public errosBipe: string | null = null;

  public dataSource = new MatTableDataSource<SerieSaidaEstoqueModalItem>([]);
  public displayedColumns: string[] = ['selecionada', 'codigoSerie'];

  public isNarrow = window.innerWidth <= 700;
  @HostListener('window:resize') onResize(): void {
    this.isNarrow = window.innerWidth <= 700;
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: SerieSaidaEstoqueModalData,
    public dialogRef: MatDialogRef<SerieSaidaEstoqueModalComponent>,
    private controleEstoqueService: ControleEstoqueService
  ) {}

  ngOnInit(): void {
    this.nomeProduto = this.data.nomeProduto;
    this.skuProduto = this.data.skuProduto ?? '';
    this.quantidadeTotal = this.data.quantidadeTotal;
    this.carregarSeries();
  }

  private carregarSeries(): void {
    this.isLoading = true;
    this.erroCarga = null;

    const request: ControleEstoqueDetalheSeriesRequest = {
      idLocalizacaoEstoque: this.data.idLocalizacaoEstoque,
      idStatusFiltro: 0,
    };

    this.controleEstoqueService.getSeries(this.data.idProduto, request, 0).subscribe({
      next: (resp) => {
        const disponiveis: SerieSaidaEstoqueModalItem[] = (resp?.list ?? [])
          .filter(s => (s.saldoDisponivel ?? 0) > 0)
          .map(s => ({
            idSerie: s.idSerie,
            codigoSerie: s.numeroSerie,
            selecionada: false,
          }));

        const savedSet = new Set<number>((this.data.series ?? [])
          .filter(s => s.selecionada).map(s => s.idSerie));
        for (const linha of disponiveis) {
          if (savedSet.has(linha.idSerie)) linha.selecionada = true;
        }

        this.dataSource.data = disponiveis;
        this.isLoading = false;
      },
      error: () => {
        this.erroCarga = 'Não foi possível carregar séries disponíveis.';
        this.dataSource.data = this.data.series ?? [];
        this.isLoading = false;
      },
    });
  }

  public get totalSelecionado(): number {
    return this.dataSource.data.filter(s => s.selecionada).length;
  }

  public get todasSeriesSelecionadas(): boolean {
    return this.dataSource.data.length > 0
      && this.totalSelecionado === this.dataSource.data.length;
  }

  public get algumaSerieSelecionada(): boolean {
    return this.totalSelecionado > 0 && !this.todasSeriesSelecionadas;
  }

  public get totalOk(): boolean {
    return this.totalSelecionado === this.quantidadeTotal;
  }

  public get statusBadge(): 'vazio' | 'parcial' | 'ok' {
    if (this.totalSelecionado === 0) return 'vazio';
    return this.totalOk ? 'ok' : 'parcial';
  }

  public bipar(): void {
    const codigo = this.bipeInput.trim();
    if (!codigo) return;

    this.errosBipe = null;
    const linha = this.dataSource.data.find(s =>
      s.codigoSerie.trim().toLowerCase() === codigo.toLowerCase());

    if (!linha) {
      this.errosBipe = `Série "${codigo}" não encontrada no estoque desta localização.`;
      return;
    }
    if (linha.selecionada) {
      this.errosBipe = `Série "${codigo}" já está selecionada.`;
      return;
    }

    linha.selecionada = true;
    this.dataSource.data = [...this.dataSource.data];
    this.bipeInput = '';
  }

  public alternarTodasSeries(): void {
    const selecionar = !this.todasSeriesSelecionadas;
    this.dataSource.data = this.dataSource.data.map(serie => ({
      ...serie,
      selecionada: selecionar,
    }));
  }

  public confirmar(): void {
    this.dialogRef.close({ confirmed: true, series: this.dataSource.data.filter(s => s.selecionada) });
  }

  public cancelar(): void {
    this.dialogRef.close({ confirmed: false });
  }
}
